import * as React from 'react';
import * as ReactDom from 'react-dom';
import { Portal } from '../../util';
import { SearchInput } from './SearchInput';
import { SearchResult } from './SearchResult';
import { Article, SearchParams, PaginationData } from '../../Model/core';
import { post } from '../../webapi';
import { IPagination } from '../../Model/interfaces';
import { Pagination } from '../Pagination';
import { Loading } from '../Loading';

type SearchContainerProps = {
    SearchInputId: string;
    SearchResultId: string;
    apiEndpoint: string;
    customPlaceholder: string | null;
}

export const SearchContainer = ({ SearchInputId, SearchResultId, apiEndpoint }: SearchContainerProps) => {
    const elm = document.querySelector(SearchInputId);

    if (!elm) {
        throw 'No element to render into';
    }

    const customPlaceholder = elm.getAttribute('data-placeholder');

    ReactDom.render(
        <App apiEndpoint={apiEndpoint} SearchInputId={SearchInputId} SearchResultId={SearchResultId} customPlaceholder={customPlaceholder} />
        , elm);
};

type RootState = {
    articles: Article[];
    searchString: string;
    loading: boolean;
    showResult: boolean;
};

class App extends React.Component<SearchContainerProps, RootState>{
    private paginationRef: any;
    private searchResultRef: any;
    constructor(props: SearchContainerProps) {
        super(props);
        this.state = { articles: [], searchString: '', loading: false, showResult: false };

        this.onSearch = this.onSearch.bind(this);
        this.fetchItems = this.fetchItems.bind(this);
        this.setSearchString = this.setSearchString.bind(this);
    }

    async fetchItems(currentPage: number): Promise<PaginationData | null> {
        const { searchString } = this.state;
        const { apiEndpoint } = this.props;
        if (searchString === '') {
            this.setState({ articles: [], loading: false, showResult: false });
            return null;
        }
        this.setState({ loading: true, articles: [] });
        const body: SearchParams = {
            SearchText: searchString,
            CurrentPage: currentPage,
            ItemsPerPage: 12
        };
        const res = await post<IPagination<Article>>(apiEndpoint, body);

        this.setState({ articles: res?.Items ?? [], loading: false, showResult: true });
        if (res.Items !== null && this.searchResultRef) {
            this.scrollToRef(this.searchResultRef);
        }
        return res?.PaginationData ?? null;
    }

    scrollToRef(ref: any) {
        window.scrollTo(0, ref.offsetTop);
    }

    setSearchString(searchString: string) {
        this.setState({ searchString });
    }

    onSearch(e: React.SyntheticEvent<HTMLButtonElement | HTMLInputElement>) {
        e.preventDefault();

        if (this.paginationRef) {
            this.paginationRef.goToPage(1);
        }
    }

    render() {
        const { SearchResultId, customPlaceholder: CustomPlaceholder } = this.props;
        const { articles, searchString, loading, showResult } = this.state;
        return (
            <>
                <SearchInput
                    onSearch={this.onSearch}
                    setSearchString={this.setSearchString}
                    searchString={searchString}
                    showEmptyResult={showResult && articles.length === 0 && !loading}
                    customPlaceholder={CustomPlaceholder}
                />
                <Portal container={SearchResultId}>
                    <div ref={ref => { this.searchResultRef = ref; }}>
                        <SearchResult items={articles} loading={loading} />
                        <Pagination
                            onPageChange={this.fetchItems}
                            queryKey="searchPage"
                            runOnMount={false}
                            ref={(ref) => { this.paginationRef = ref; }}
                        />
                    </div>
                </Portal>
            </>
        );
    }
}